import * as Yup from 'yup';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import palette from 'src/theme/palette';
import InputField from 'src/components/InputField';
import { Form, FormikProvider, useFormik } from 'formik';
import CustomDropdown from 'src/components/CustomDropdown';
import CustomDatePicker from 'src/components/CustomDatePicker';
import { api } from 'src/Api';
import { currencyData } from 'src/asset/Currency';
import { toast } from 'react-toastify';
import LoadingButtonWrapper from 'src/components/LoadingButtonWrapper';
import moment from 'moment';

const EditAddModal = ({ open, onClose, data, isEdit, refresh }) => {
  const token = localStorage.getItem('token');
  const [saveLoading, setSaveLoading] = useState(false);

  const EditAddSchema = Yup.object().shape({
    firstname: Yup.string().required('First Name is required'),
    lastname: Yup.string().required('Last Name is required'),
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    contact_no: Yup.string().required('Contact no is required')
  });

  const formik = useFormik({
    enableReinitialize: true, // ✅ Add this line
    initialValues: {
      firstname: data?.firstname || '',
      lastname: data?.lastname || '',
      email: data?.email || '',
      contact_no: data?.contact_no || ''
    },
    validationSchema: EditAddSchema,
    onSubmit: () => {
      handleSave();
    }
  });

  const { errors, touched, values, handleSubmit, getFieldProps } = formik;

  function handleSave() {
    setSaveLoading(true);
    const token = localStorage.getItem('token');
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    data?.id && formData.append('user_id', data?.id);
    values?.firstname && formData.append('firstname', values?.firstname);
    values?.lastname && formData.append('lastname', values?.lastname);
    values?.email && formData.append('email', values?.email);
    values?.contact_no && formData.append('contact_no', values?.contact_no);

    const requestOptions = {
      method: 'Post',
      redirect: 'follow',
      headers: myHeaders,
      body: formData
    };

    fetch(api.edit_user, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          toast.success(result.message);
          onClose();
          refresh();
          formik.resetForm();
        } else {
          toast.error(result.error);
        }
      })
      .catch((error) => {
        toast.error(error.error);
        console.log('error', error);
      })
      .finally(() => {
        setSaveLoading(false);
      });
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        formik.resetForm(); // Reset form on modal close
        onClose();
      }}
      PaperProps={{
        sx: {
          width: '700px', // or any custom width
          maxWidth: 'none'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: 2,
          paddingRight: 2,
          paddingTop: 1
        }}
      >
        <Typography
          sx={{ fontSize: 16, color: palette.primary.titledark, fontFamily: 'poppinsBold' }}
        >
          {data ? 'Edit' : 'Add'} user
        </Typography>
        <IconButton
          onClick={() => {
            formik.resetForm(); // Reset form on close button click
            onClose();
          }}
        >
          <CloseIcon style={{ color: palette.primary.disabled, fontSize: 20 }} />
        </IconButton>
      </DialogTitle>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogContent
            sx={{
              paddingLeft: 2,
              paddingRight: 4,
              paddingTop: 0
            }}
          >
            <Grid
              container
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              {/* FIRST NAME */}
              <Grid item xs={5.8}>
                <InputField
                  name="firstname"
                  label="First Name"
                  type="text"
                  placeholder=""
                  {...getFieldProps('firstname')}
                  error={touched.firstname && errors.firstname}
                  helperText={touched.firstname && errors.firstname}
                  fontFamily="poppinsRegular"
                  labelFontMedium
                />
              </Grid>

              {/* LAST NAME */}
              <Grid item xs={5.8}>
                <InputField
                  name="lastname"
                  label="Last Name"
                  type="text"
                  placeholder=""
                  {...getFieldProps('lastname')}
                  error={touched.lastname && errors.lastname}
                  helperText={touched.lastname && errors.lastname}
                  fontFamily="poppinsRegular"
                  labelFontMedium
                />
              </Grid>

              {/* EMAIL */}
              <Grid item xs={5.8}>
                <InputField
                  disabled={isEdit}
                  name="email"
                  label="Email"
                  type="text"
                  placeholder=""
                  {...getFieldProps('email')}
                  error={touched.email && errors.email}
                  helperText={touched.email && errors.email}
                  fontFamily="poppinsRegular"
                  labelFontMedium
                />
              </Grid>

              <Grid item xs={5.8}>
                <InputField
                  name="contact_no"
                  label="Cotact No"
                  type="number"
                  placeholder=""
                  {...getFieldProps('contact_no')}
                  error={touched.contact_no && errors.contact_no}
                  helperText={touched.contact_no && errors.contact_no}
                  fontFamily="poppinsRegular"
                  labelFontMedium
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ mb: 1, mr: 1.5 }}>
            <Button
              onClick={() => {
                formik.resetForm(); // Reset form on close button click
                onClose();
              }}
              variant="outlined"
              sx={{
                ml: 1.5,
                color: palette.primary.pageButtonTitle,
                borderColor: palette.primary.pageButtonBorder,
                fontSize: 12,
                height: 30,
                fontFamily: 'poppinsRegular',
                borderRadius: 0.7,
                '&:hover': {
                  backgroundColor: palette.primary.inputborder,
                  borderColor: palette.primary.pageButtonBorder
                }
              }}
            >
              Cancel
            </Button>
            <LoadingButtonWrapper
              loading={saveLoading}
              type="submit"
              variant="contained"
              sx={{
                width: saveLoading ? 120 : 90,
                height: 30,
                fontSize: 12,
                borderRadius: 0.7,
                fontFamily: 'poppinsLight',
                letterSpacing: 0.3,
                transition: 'width 0.3s ease, background-color 0.3s ease, transform 0.3s ease'
              }}
              title="Save"
            />
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
};
export default EditAddModal;
